import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Building } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

interface UserTypeSelectionProps {
  title?: string;
  subtitle?: string;
}

export default function UserTypeSelection({ 
  title = "Get started as a...",
  subtitle = "Choose your account type to continue"
}: UserTypeSelectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const handleUserTypeSelect = (userType: 'family' | 'provider') => {
    if (userType === 'provider') {
      sessionStorage.setItem('userType', 'owner');
      navigate('/auth/owner-register');
      return;
    }

    // Family flow: preserve existing query parameters and add userType
    searchParams.set('userType', userType);
    sessionStorage.setItem('userType', userType);
    const currentAction = location.pathname.includes('signup') ? 'signup' : 'login';
    const targetPath = currentAction === 'signup' ? '/auth/signup' : '/auth/login';
    
    navigate(`${targetPath}?${searchParams.toString()}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const userTypes = [
    {
      type: 'family' as const,
      title: 'Looking for Care',
      description: 'I\'m searching for nursing home care for myself or a family member',
      icon: Heart,
      color: 'bg-primary',
      hoverColor: 'hover:bg-primary/90',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary'
    },
    {
      type: 'provider' as const,
      title: 'Nursing Home Provider',
      description: 'I represent a nursing home or care facility',
      icon: Building,
      color: 'bg-secondary',
      hoverColor: 'hover:bg-secondary/90',
      iconBg: 'bg-secondary/10',
      iconColor: 'text-secondary'
    }
  ];

  return (
    <motion.div 
      className="w-full max-w-2xl mx-auto"
      initial="hidden"
      animate="show"
      variants={containerVariants}
    >
      <motion.div 
        className="text-center mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {title}
        </h2>
        <p className="text-muted-foreground">
          {subtitle}
        </p>
      </motion.div>

      <motion.div 
        className="grid md:grid-cols-2 gap-4"
        variants={containerVariants}
      >
        {userTypes.map((userType) => {
          const Icon = userType.icon;
          
          return (
            <motion.div key={userType.type} variants={itemVariants} className="flex">
              <Card 
                className="p-6 cursor-pointer transition-all duration-200 hover:shadow-lg border-2 hover:border-primary/20 group flex-1 flex flex-col"
                onClick={() => handleUserTypeSelect(userType.type)}
              >
                <div className="text-center space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className={`w-16 h-16 rounded-full ${userType.iconBg} flex items-center justify-center mx-auto group-hover:scale-105 transition-transform duration-200`}>
                      <Icon className={`w-8 h-8 ${userType.iconColor}`} />
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {userType.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {userType.description}
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    className={`w-full ${userType.color} ${userType.hoverColor} text-white font-medium mt-4`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUserTypeSelect(userType.type);
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      <motion.div 
        className="text-center mt-6"
        variants={itemVariants}
      >
        <p className="text-xs text-muted-foreground">
          You can change this later in your account settings
        </p>
      </motion.div>
    </motion.div>
  );
}